import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { Alert, Button, IconButton, Spinner, Text } from '../design/index.js';
import { acceptInvite, declineInvite, fetchIncomingInvites } from '../api.js';
import type { InviteSummary } from '../api.js';
import { useDismissable } from './useDismissable.js';
import styles from './NotificationMenu.module.css';

/** While the menu stays open, re-read this often at most — a new invite is
 *  not urgent enough to justify a tighter timer, and CLAUDE.md's Withings
 *  section is explicit that nothing here should poll forever: this interval
 *  is cleared the moment the menu closes, and never starts before it opens. */
const POLL_MS = 60_000;

export interface NotificationMenuProps {
  /** Fired after an invitation is accepted, so the orbit and person list can
   *  be re-read: accepting is what makes a new person appear. */
  onAccepted?: () => void;
  className?: string;
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; invites: InviteSummary[] };

/** Per-invite outcome of an in-place Accept/Decline, keyed by invite id.
 *  Kept separate from `ListState` so acting on one row never has to redraw
 *  the whole list — the row itself is what changes. */
type RowState =
  | { kind: 'idle' }
  | { kind: 'busy'; action: 'accept' | 'decline' }
  | { kind: 'done'; action: 'accept' | 'decline' }
  | { kind: 'error'; action: 'accept' | 'decline'; message: string };

function nodeList(nodes: InviteSummary['nodes']): string {
  return nodes.map((node) => node.title).join(', ');
}

export function NotificationMenu({ onAccepted, className }: NotificationMenuProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const invites = await fetchIncomingInvites();
      setState({ kind: 'ready', invites });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not load your notifications.',
      });
    }
  }, []);

  // Pending count drives both the badge and the button's accessible name.
  // A row already actioned in this session (accepted/declined) stops
  // counting immediately, rather than waiting on the next poll or reopen to
  // catch up with what the server now thinks.
  const pendingCount =
    state.kind === 'ready'
      ? state.invites.filter((invite) => {
          const row = rowStates[invite.id];
          return invite.status === 'pending' && (!row || row.kind === 'idle' || row.kind === 'error');
        }).length
      : 0;

  /**
   * Read once on mount, so the bell can carry a count before anyone clicks it.
   *
   * Without this the badge only ever appeared after the menu had been opened,
   * which is precisely backwards: the badge exists so you DON'T have to open
   * the menu to find out whether anything is waiting. One request per page
   * load, and no timer — the polling below still runs only while the menu is
   * open.
   */
  useEffect(() => {
    void load();
  }, [load]);

  const openMenu = useCallback(() => {
    setOpen(true);
    setRowStates({});
    setState({ kind: 'loading' });
    void load();
  }, [load]);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    if (open) closeMenu();
    else openMenu();
  }, [open, openMenu, closeMenu]);

  // Poll while open, at most once a minute; stop the moment it closes so
  // nothing keeps ticking in the background.
  useEffect(() => {
    if (!open) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => {
      void load();
    }, POLL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [open, load]);

  // Escape, outside-click and focus handling all live in `useDismissable`
  // now — this menu is where that behaviour was written, and it moved out
  // wholesale the moment a second panel needed the same thing. Nothing here
  // changed in the move, including the `wasOpen` guard that stops the first
  // render pulling focus to the bell.
  const { triggerRef: buttonRef, panelRef: menuRef } = useDismissable<
    HTMLButtonElement,
    HTMLDivElement
  >(open, closeMenu);

  async function act(invite: InviteSummary, action: 'accept' | 'decline') {
    setRowStates((prev) => ({ ...prev, [invite.id]: { kind: 'busy', action } }));
    try {
      if (action === 'accept') {
        await acceptInvite(invite.id);
        onAccepted?.();
      } else {
        await declineInvite(invite.id);
      }
      setRowStates((prev) => ({ ...prev, [invite.id]: { kind: 'done', action } }));
    } catch (err) {
      setRowStates((prev) => ({
        ...prev,
        [invite.id]: {
          kind: 'error',
          action,
          message: err instanceof Error ? err.message : `Could not ${action} this invite.`,
        },
      }));
    }
  }

  function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    // Handled at the document level too (so Escape works even if focus has
    // moved to something not nested under the menu's own ref), but stopping
    // it here as well keeps it from bubbling somewhere else that also
    // listens for Escape.
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeMenu();
    }
  }

  const accessibleName = pendingCount > 0 ? `Notifications, ${pendingCount} waiting` : 'Notifications';

  return (
    <div className={className}>
      <div className={styles.wrap}>
        <IconButton
          ref={buttonRef}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={accessibleName}
          onClick={toggle}
        >
          <BellIcon />
          {pendingCount > 0 && (
            <span className={styles.badge} aria-hidden="true">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </IconButton>

        {open && (
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label="Notifications"
            tabIndex={-1}
            onKeyDown={handleMenuKeyDown}
          >
            {state.kind === 'loading' && (
              <div className={styles.status}>
                <Spinner size="sm" aria-label="Loading your notifications" />
                <Text size="sm" color="secondary">Loading…</Text>
              </div>
            )}

            {state.kind === 'error' && (
              <Alert variant="error" title="Couldn't load your notifications" data-testid="notification-menu-error">
                {state.message}
              </Alert>
            )}

            {state.kind === 'ready' && state.invites.length === 0 && (
              <div className={styles.status}>
                <Text size="sm" color="secondary">Nothing waiting.</Text>
              </div>
            )}

            {state.kind === 'ready' && state.invites.length > 0 && (
              <ul className={styles.list}>
                {state.invites.map((invite) => {
                  const row = rowStates[invite.id] ?? { kind: 'idle' as const };
                  const busy = row.kind === 'busy';
                  return (
                    <li key={invite.id} className={styles.row} role="none">
                      <Text size="sm" weight="medium" as="div">
                        {invite.fromDisplayName}
                      </Text>
                      <Text size="xs" color="secondary" as="div">
                        Sharing: {nodeList(invite.nodes)}
                      </Text>

                      {row.kind === 'done' ? (
                        <Text size="sm" color={row.action === 'accept' ? 'positive' : 'secondary'}>
                          {row.action === 'accept' ? 'Accepted.' : 'Declined.'}
                        </Text>
                      ) : (
                        <div className={styles.actions}>
                          <Button
                            type="button"
                            size="sm"
                            role="menuitem"
                            disabled={busy}
                            onClick={() => act(invite, 'accept')}
                          >
                            {row.kind === 'busy' && row.action === 'accept' ? 'Accepting…' : 'Accept'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            role="menuitem"
                            disabled={busy}
                            onClick={() => act(invite, 'decline')}
                          >
                            {row.kind === 'busy' && row.action === 'decline' ? 'Declining…' : 'Decline'}
                          </Button>
                        </div>
                      )}

                      {row.kind === 'error' && (
                        <Alert variant="error" data-testid="notification-menu-row-error">
                          {row.message}
                        </Alert>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
